import { debounce } from 'underscore'
import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import PlotlyPlot from 'react-plotly.js'
import styled from 'styled-components'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'

import alertsPlotBandBuilder from 'pages/Device/ThermostatRthPanel/Chart/alertsPlotBandBuilder'
import { chartOptionsBuilder, buildTraceAndConfigChart } from 'pages/shared/MaintenanceTrend/chartOptions/chartOptionsBuilder'
import { formatFirstAndLastTickLabels } from 'pages/shared/MaintenanceTrend/tickLabelformatHelper'
import HoverBox from 'pages/shared/MaintenanceTrend/HoverBox'
import { parseTooltipData } from 'pages/shared/MaintenanceTrend/parseTooltipData'

import useScreenSize from 'hooks/useScreenSize'

import GA4 from 'helpers/ga4'
import { chartHeight, xValuePositionInPixel } from 'helpers/plotly/utils'

import { color } from 'styles/colors'

import { DeviceMaintenanceTrend, Trend, DeviceMaintenanceTrendGroup, DeviceMetric } from 'types/DeviceMetric'
import { Alert } from 'types/Alert'

type props = {
  trend: DeviceMaintenanceTrend | Trend
  metricAndGroupList: Array< DeviceMaintenanceTrendGroup | DeviceMetric>
  alerts: Array<Alert>
  timeZone: string
  deviceId: string
  sendAnalyticsEvents: boolean
  tier?: string | null
  tooltipId: string
}

const PlotTrend = ({ timeZone, trend, metricAndGroupList, alerts, deviceId, sendAnalyticsEvents, tier, tooltipId }: props) => {
  const graphDivRef = useRef<Readonly<HTMLElement>>()
  const containerRef = useRef(null)
  const trendNameSlug = trend.name.toLowerCase().replace(/ /g, '-')
  const chartDivId = `${trendNameSlug}trend_plotly_chart`
  const selectedLegendAlerts = useMemo(() => ['critical', 'major', 'dirty-filter', 'minor', 'major-sda', 'minor-sda'], [])
  const screenSize = useScreenSize()
  const [relayout, setRelayout] = useState<number>(1)
  const [isHovering, setIsHovering] = useState(false)
  const [top, setTop] = useState(0)
  const [left, setLeft] = useState(0)
  const [tooltipData, setTooltip] = useState<Array<{ color: string, value: string, name: string, showInTooltip: boolean }> | null>(null)
  const [hasBeenHovered, setHasBeenHovered] = useState(false)
  const hasAlerts = alerts.length > 0
  const hasData = metricAndGroupList.some((item) => item.values && item.values.length > 0)
  const screenWidth = window.innerWidth

  const { traces, yAxisConfig } = useMemo(() => {
    return buildTraceAndConfigChart(metricAndGroupList, hasAlerts, trend, screenSize, screenWidth)
  }, [metricAndGroupList, hasAlerts, trend, screenSize, screenWidth])

  const { layout, config } = useMemo(() => {
    return chartOptionsBuilder.build(traces, yAxisConfig, timeZone, screenSize, hasAlerts, trend, hasData)
  }, [traces, yAxisConfig, timeZone, screenSize, hasAlerts, trend, hasData])

  const updateAlerts = useCallback(() => {
    if (graphDivRef.current && alerts.length > 0) { alertsPlotBandBuilder.addOrUpdateAlerts(`${trendNameSlug}trend-alerts-group`, layout, chartDivId, alerts, selectedLegendAlerts, undefined, tooltipId) }
  }, [trendNameSlug, layout, chartDivId, alerts, selectedLegendAlerts, tooltipId])

  useEffect(() => {
    const observer = new ResizeObserver(debounce(() => {
      setRelayout(Date.now().valueOf())
      if (graphDivRef.current && alerts.length > 0) {
        setTimeout(() => updateAlerts(), 100)
      }
    }, 500))
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect() // unsubscribe the event on UI element removed from dom
  }, [alerts.length, updateAlerts])

  const formatTickLabels = () => {
    formatFirstAndLastTickLabels(layout, graphDivRef.current)
  }

  const onChartLoad = useCallback((graphDiv: Readonly<HTMLElement>) => {
    graphDivRef.current = graphDiv
    setTimeout(() => {
      if (alerts.length > 0 && graphDivRef.current) {
        updateAlerts()
      }
    }, 100)
  }, [alerts.length, updateAlerts])

  const onHover = (e: Readonly<Plotly.PlotHoverEvent>, layout: Partial<Plotly.Layout>) => {
    e.event.preventDefault()
    const widthOfLeftYAxisInPercentOfPlotWidth = 0.018
    const plotWidth = document.getElementById(chartDivId)?.getBoundingClientRect().width
    const offsetFromLeftYAxis = plotWidth && widthOfLeftYAxisInPercentOfPlotWidth * plotWidth

    setTop(e.event.offsetY)
    setLeft(xValuePositionInPixel(e.points[0].xaxis, e.points[0].x, layout) + offsetFromLeftYAxis)
    setTooltip(parseTooltipData(e.points, metricAndGroupList))
    setIsHovering(true)

    if (sendAnalyticsEvents && !hasBeenHovered) {
      GA4.sendEvent({
        category: 'Maintenance Trends Page',
        action: 'Maintenance Trend Hover',
        label: `${deviceId}, ${trend.name}, ${tier}`
      })
      setHasBeenHovered(true)
    }
  }

  const onUnhover = (e: Readonly<Plotly.PlotMouseEvent>) => {
    e.event.preventDefault()

    setTooltip(null)
    setIsHovering(false)
  }

  return (
    <S.Container ref={containerRef}>
      <S.PlotTrend
        divId={chartDivId}
        data={traces}
        layout={layout}
        config={config}
        onInitialized={(_, d) => onChartLoad(d)}
        onAfterPlot={() => formatTickLabels()}
        onHover={(e) => onHover(e, layout)}
        onUnhover={onUnhover}
        revision={relayout}
      />

      {isHovering && (
        <S.ToolTipCrossHair left={left} width={2} height={chartHeight(graphDivRef.current) - 50}>
          <S.ArrowDropUpIcon top={chartHeight(graphDivRef.current) - 70} />
        </S.ToolTipCrossHair>
      )}
      {isHovering && tooltipData && <HoverBox top={top} left={left} tooltipData={tooltipData} />}
    </S.Container>
  )
}

export default memo(PlotTrend)

const S = {
  Container: styled.div`
    position: relative;
    pointer-events: none;
    margin: 0px;
  `,
  PlotTrend: styled(PlotlyPlot)`
    width: 100%;
    height: 350px;
  `,
  ArrowDropUpIcon: styled(ArrowDropUpIcon).withConfig({
    shouldForwardProp: (prop) => !['top'].includes(prop)
  })<{ top: number }>`
    font-size: 50px;
    margin-left: -24px;
    position: absolute;
    pointer-events: none;
    top:  ${({ top }) => (`${top}px`)};;
  `,
  ToolTipCrossHair: styled.div.withConfig({
    shouldForwardProp: (prop) => !['height', 'width', 'left'].includes(prop)
  })<{ height: number, width: number, left: number }>`
    background-color: ${color('rthLegendBorderHover')};
    height: ${({ height }) => (`${height}px`)};
    width: ${({ width }) => (`${width}px`)};
    pointer-events: none;
    position: absolute;
    left: ${({ left, width }) => (`${left - (width * 0.5)}px`)};
    top: 0;
    z-index: 1;
  `
}
